import React, { useEffect, useRef, useState, createContext, useContext } from 'react';
import { DrawerCustomer } from '../components/drawer/customer';
import { getCustomers, getCustomer, getCustomerByRef } from '../shared/common.api';
import { UserStateContext } from './user-state-context';
import { toasterService } from 'components/toaster/toaster-service';
import { OPEN_CUSTOMER_EVENT } from 'components/cinch-ai/cinch-ai-events';
export const CustomerDrawerContext = createContext();
export const CustomerDrawerContextProvider = ({
  children
}) => {
  const {
    asCompany,
    user,
    redirect,
    checkCompany,
    hasIntegration,
    hasPermission,
    logOut,
    setAsCompany,
    setToken,
    setTokenExpiry,
    hasMultiAccess,
    setUser,
    setRedirect,
    isValidToken
  } = useContext(UserStateContext);
  const [isLoadingByRef, setIsLoadingByRef] = useState(false);
  const [customerID, setCustomerID] = useState();
  const [customerTransaction, setCustomerTransaction] = useState();
  const [customerSchedule, setCustomerSchedule] = useState();
  // CIN-7048: the task to open in Details → Tasks, from openCustomerDrawer(id, 'tasks', taskId) or ?task_id=.
  const [customerTask, setCustomerTask] = useState();
  const [onCloseCallback, setOnCloseCallback] = useState();
  const [startTab, setStartTab] = useState('summary');
  const [scrollToTransactionDetails, setScrollToTransactionDetails] = useState(false);
  // CINCHAI-142: Cinch AI can open the drawer at a timeline event, or on Messaging with a draft in the box.
  const [customerTimelineEvent, setCustomerTimelineEvent] = useState(null);
  const [customerDraft, setCustomerDraft] = useState(null);
  useEffect(() => {
    if (!asCompany?.is_active) {
      // We may need to figure out a better way to load everything like maybe a global loader page.
      // but checking for is_active is enough to prevent trying to load data before our token is ready.
      return;
    }
    const params = new URLSearchParams(window.location.search);
    if (params.has('customer_drawer_tab')) {
      setStartTab(params.get('customer_drawer_tab'));
    }
    if (params.has('transaction_id')) {
      setCustomerTransaction(params.get('transaction_id'));
    }
    if (params.has('schedule_id')) {
      setCustomerSchedule(params.get('schedule_id'));
    }
    if (params.has('task_id')) {
      setCustomerTask(params.get('task_id'));
    }
    if (params.has('customer_drawer_id')) {
      getCustomer(params.get('customer_drawer_id')).then(customer => {
        setCustomerID(customer.data.id);
      }).catch(e => {
        toasterService.error('No customer was found with that id');
      });
    } else if (params.has('customer_drawer_ref') && !params.has('customer_drawer_entity')) {
      toasterService.error('Please also include a customer_drawer_entity URL param for the lookup');
    } else if (!params.has('customer_drawer_ref') && params.has('customer_drawer_entity')) {
      toasterService.error('Please also include a customer_drawer_ref URL param for the lookup');
    } else if (params.has('customer_drawer_ref') && params.has('customer_drawer_entity')) {
      getCustomers({
        company: asCompany.id,
        customer_refs__entity_ref: params.get('customer_drawer_ref'),
        customer_refs__entity_source: params.get('customer_drawer_entity'),
        limit: 1
      }).then(({
        data
      }) => {
        if (data.results.length > 0) {
          setCustomerID(data.results[0].id);
        } else {
          toasterService.error('No customer was found with those ids');
        }
      });
    }
  }, [asCompany?.is_active]);
  const addCustomerParam = (id, tab) => {
    let queryParams = new URLSearchParams(window.location.search);
    queryParams.set('customer_drawer_id', id);
    queryParams.set('customer_drawer_tab', tab ? tab : startTab);
    let newurl = window.location.protocol + '//' + window.location.host + window.location.pathname + '?' + queryParams.toString();
    window.history.pushState({
      path: newurl
    }, '', newurl);
  };
  const removeCustomerParam = () => {
    let queryParams = new URLSearchParams(window.location.search);
    queryParams.delete('customer_drawer_id');
    queryParams.delete('customer_drawer_ref');
    queryParams.delete('customer_drawer_entity');
    queryParams.delete('customer_drawer_tab');
    if (Array.from(queryParams).length) {
      let newurl = window.location.protocol + '//' + window.location.host + window.location.pathname + '?' + queryParams.toString();
      window.history.pushState({
        path: newurl
      }, '', newurl);
    } else {
      let newurl = window.location.protocol + '//' + window.location.host + window.location.pathname;
      window.history.pushState({
        path: newurl
      }, '', newurl);
    }
  };
  const setTabParam = tab => {
    let queryParams = new URLSearchParams(window.location.search);
    queryParams.set('customer_drawer_tab', tab);
    let newurl = window.location.protocol + '//' + window.location.host + window.location.pathname + '?' + queryParams.toString();
    window.history.pushState({
      path: newurl
    }, '', newurl);
  };
  const openCustomerDrawerByRef = (entityRef, entitySource, tab, onCloseCallback) => {
    if (tab) {
      setStartTab(tab);
    }
    setIsLoadingByRef(true);
    setOnCloseCallback(() => onCloseCallback);
    getCustomerByRef(entityRef, entitySource).then(({
      data
    }) => {
      setCustomerID(data.id);
    }).catch(err => {
      if (err?.response?.status === 404) {
        setCustomerID('NOT_FOUND');
      }
    }).finally(() => {
      setIsLoadingByRef(false);
    });
  };
  const openCustomerDrawer = (customerID, tab, objectId, transactionScrollDown, onCloseCallback) => {
    if (tab) {
      setStartTab(tab);
    }
    setCustomerTimelineEvent(null);
    setCustomerDraft(null);
    setOnCloseCallback(() => onCloseCallback);
    setCustomerTransaction(tab === 'transactions' ? objectId : null);
    setCustomerSchedule(tab === 'schedules' ? objectId : null);
    setCustomerTask(tab === 'tasks' ? objectId : null);
    setScrollToTransactionDetails(transactionScrollDown);
    setCustomerID(customerID);
    addCustomerParam(customerID, tab);
  };

  // Cinch AI's open_customer action (CINCHAI-142) arrives as a window event: the Cinch AI provider sits
  // outside this one, so it can't call openCustomerDrawer itself. It has already re-checked permissions.
  const openFromCinchAi = useRef(null);
  openFromCinchAi.current = action => {
    const open = () => {
      openCustomerDrawer(action.customer_id, action.tab, action.object_id);
      setCustomerTimelineEvent(action.timeline_event_id || null);
      setCustomerDraft(action.draft || null);
    };
    if (customerID && String(customerID) === String(action.customer_id)) {
      // Same customer already open: close and reopen so the tab, event and draft apply.
      setCustomerID(null);
      setTimeout(open, 0);
    } else {
      open();
    }
  };
  useEffect(() => {
    const onOpen = e => openFromCinchAi.current(e.detail || {});
    window.addEventListener(OPEN_CUSTOMER_EVENT, onOpen);
    return () => window.removeEventListener(OPEN_CUSTOMER_EVENT, onOpen);
  }, []);
  const closeCustomerDrawer = () => {
    removeCustomerParam();
    setCustomerID(null);
    if (onCloseCallback) {
      onCloseCallback();
      setOnCloseCallback(null);
    }
  };
  return <CustomerDrawerContext.Provider value={{
    openCustomerDrawer,
    openCustomerDrawerByRef,
    closeCustomerDrawer,
    asCompany,
    user,
    redirect,
    checkCompany,
    hasIntegration,
    hasPermission,
    logOut,
    setAsCompany,
    setToken,
    setTokenExpiry,
    hasMultiAccess,
    setUser,
    setRedirect,
    isValidToken,
    customerID
  }}>
      {children}
      <DrawerCustomer isOpen={!!customerID || isLoadingByRef} customerID={customerID} onTabChange={tab => {
      setTabParam(tab);
      setStartTab(tab);
    }} onClose={() => {
      removeCustomerParam();
      setCustomerID(null);
      setStartTab('summary');
      setCustomerTimelineEvent(null);
      setCustomerDraft(null);
      if (onCloseCallback) {
        onCloseCallback();
        setOnCloseCallback(null);
      }
    }} startTab={startTab} startTransaction={customerTransaction} startSchedule={customerSchedule} startTask={customerTask} startTimelineEvent={customerTimelineEvent} startDraft={customerDraft}
    // Used once: coming back to Messaging (after sending) never refills it.
    onDraftUsed={() => setCustomerDraft(null)} scrollToTransactionDetails={scrollToTransactionDetails} />
    </CustomerDrawerContext.Provider>;
};